/**
 * The recipe library's domain types, shared by the API, the import, the chat
 * function and the SPA. See docs/architecture.md §3 and §5.
 *
 * Nothing here touches the table: these are the shapes stored on `ING#` and
 * `SPEC#` items and the pure helpers that read them. Costing itself lives in
 * calc.ts; `specTotals` is the bridge from a stored spec to those functions.
 */
import { buildTotals } from "./calc.js";
/** The colour for a line with no aroma family: water, ice, soda. */
export const NEUTRAL_COLOUR = "#6B625A";
/** One dash of bitters, in millilitres. Matches the prototype. */
export const DASH_ML = 0.8;
/** One drop, in millilitres. Used for violette and the like. */
export const DROP_ML = 0.1;
/**
 * What a "top with" line is assumed to contribute when the recipe does not
 * say. Sixty millilitres is a small highball's worth; it is flagged so the
 * editor can ask.
 */
export const TOP_ML = 60;
export const SPEC_STATUSES = [
    "available",
    "not-active",
    "needs-testing",
    "retired",
];
/** As `SOURCES` below: a record, so a kind added to the type must be listed. */
const KINDS = {
    spirit: true,
    liqueur: true,
    fortified: true,
    bitters: true,
    juice: true,
    syrup: true,
    mixer: true,
    dairy: true,
    dilution: true,
    other: true,
};
export const INGREDIENT_KINDS = Object.keys(KINDS);
export function isIngredientKind(value) {
    return typeof value === "string" && Object.hasOwn(KINDS, value);
}
/**
 * Every source, as a record rather than a list, so the two cannot drift.
 *
 * They did: the API's validator was written with four of the five and left
 * `workbook` out, which refused every save of an ingredient that came in
 * from the workbook — most of Above Board's shelf — with "source is
 * invalid", because the screen sends the stored record back as it found it.
 * `Record<IngredientSource, true>` makes a source added to the type a
 * compile error here until it is listed.
 */
const SOURCES = {
    catalogue: true,
    airtable: true,
    workbook: true,
    manual: true,
    chat: true,
};
export const INGREDIENT_SOURCES = Object.keys(SOURCES);
export function isIngredientSource(value) {
    return typeof value === "string" && Object.hasOwn(SOURCES, value);
}
export const MAX_TALK_ONE = 300;
export const MAX_TALK_TWO = 700;
export const MAX_TALK_THREE = 2500;
/** True when any of the three is written. */
export function hasTalk(talk) {
    return !!talk && (talk.one.trim() !== "" || talk.two.trim() !== "" || talk.three.trim() !== "");
}
/** As `SOURCES`: a record, so a unit added to the type must be listed. */
const UNITS = {
    ml: true,
    dash: true,
    drop: true,
    top: true,
    each: true,
    gram: true,
};
export const LINE_UNITS = Object.keys(UNITS);
export function isLineUnit(value) {
    return typeof value === "string" && Object.hasOwn(UNITS, value);
}
/** The most tags one spec may carry. A filter stops being a filter past this. */
export const MAX_TAGS = 24;
/** The longest a single tag may be. Long enough for "non-alcoholic". */
export const MAX_TAG_LENGTH = 32;
/**
 * Tidy a list of tags into the form they are stored and compared in.
 *
 * Lowercased, inner whitespace collapsed, trimmed, empties dropped,
 * de-duplicated with the first spelling winning, and capped. Case and
 * spacing are the whole reason this exists: `Tiki`, `tiki ` and `TIKI` are
 * one idea, and without folding them the Library grows three chips for it
 * and a filter on one misses the other two.
 *
 * Deliberately not slugified. A tag is read by people — "non-alcoholic"
 * should not become "non-alcoholic" via `non-alcoholic` and come back
 * looking like an id. Punctuation a bar actually types is kept.
 */
export function normaliseTags(tags) {
    const seen = new Set();
    for (const raw of tags) {
        if (typeof raw !== "string")
            continue;
        const tag = raw.toLowerCase().replace(/\s+/g, " ").trim().slice(0, MAX_TAG_LENGTH).trim();
        if (tag)
            seen.add(tag);
        if (seen.size >= MAX_TAGS)
            break;
    }
    return [...seen];
}
/**
 * Turn a name into a stable id: `H.S.L Special` → `hsl-special`,
 * `D.O.M. Bénédictine` → `dom-benedictine`.
 *
 * Deterministic so that re-running an import converges on the same items.
 * Dots and apostrophes are dropped rather than turned into separators, so
 * initialisms stay one word.
 */
export function slugify(name) {
    const slug = name
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[.'’]/g, "")
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    return slug || "item";
}
/** The id of the primary a spec belongs under: its own, for a primary. */
export function primaryIdOf(spec) {
    return spec.variantOf || spec.specId;
}
/** The variants of one primary, by name. */
export function variantsOf(specs, primaryId) {
    return specs
        .filter((s) => s.variantOf === primaryId && s.specId !== primaryId)
        .sort((a, b) => a.name.localeCompare(b.name));
}
/**
 * Why `specId` cannot be saved as a variant of `variantOf`, or null when it
 * can. Null `variantOf` — a primary — is always fine.
 *
 * One level, because a library is read as primaries with their variants
 * underneath and a third level has nowhere to go: the primary must exist,
 * must not itself be a variant, and the spec being moved under it must not
 * have variants of its own.
 */
export function variantProblem(specId, variantOf, specs) {
    if (!variantOf)
        return null;
    if (variantOf === specId)
        return "A spec cannot be a variant of itself";
    const primary = specs.find((s) => s.specId === variantOf);
    if (!primary)
        return `No spec ${variantOf} to be a variant of`;
    if (primary.variantOf) {
        return `${primary.name} is itself a variant; choose its primary instead`;
    }
    const own = variantsOf(specs, specId);
    if (own.length > 0) {
        return `This spec has ${own.length} variant${own.length === 1 ? "" : "s"} of its own, so it cannot become one`;
    }
    return null;
}
/**
 * Specs in library order: each primary followed by its variants. `order`
 * sorts the primaries — and the variants under each — and defaults to name.
 *
 * A variant whose primary is not in `specs` (filtered out, or gone) is
 * listed on its own in the primaries' order rather than dropped.
 */
export function familyOrder(specs, order = (a, b) => a.name.localeCompare(b.name)) {
    const ids = new Set(specs.map((s) => s.specId));
    const heads = specs.filter((s) => !s.variantOf || !ids.has(s.variantOf)).sort(order);
    const out = [];
    for (const head of heads) {
        out.push(head);
        if (head.variantOf)
            continue;
        out.push(...specs.filter((s) => s.variantOf === head.specId).sort(order));
    }
    return out;
}
/** Four random bytes as hex. 4.3 billion values; see `newVenueId`. */
function randomSuffix() {
    const bytes = crypto.getRandomValues(new Uint8Array(4));
    return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}
/**
 * A fresh venue id: a readable half and a unique half.
 *
 *   newVenueId("Collingwood")  →  "collingwood_a1b2c3d4"
 *
 * Deliberately NOT `slugify` alone, and deliberately not opaque either.
 *
 * Not slugify alone, because a venue id is half of a partition key and a name
 * is not unique. Two customers each with a "Public Bar" would otherwise share
 * one partition — see `venuePk` in keys.ts.
 *
 * Not opaque, because venue ids appear in logs, S3 slip paths and the console,
 * and `collingwood_a1b2c3d4` can be read at a glance where a bare id cannot.
 *
 * The cost of the readable half is that it can go stale: rename Collingwood to
 * Fitzroy and the id still says collingwood. The display name lives on the
 * venue's META, so this shows only in keys and logs, and venues are renamed
 * rarely. Accepted deliberately.
 *
 * Note this is the one id in the system that is **not** derived purely from
 * its name, so unlike `slugify` it does not converge on re-running. Callers
 * mint it once, at creation, and store it. The suffix is injectable so tests
 * can be deterministic.
 */
export function newVenueId(name, suffix = randomSuffix()) {
    // Built from the slug plus the suffix rather than slugifying the whole
    // string: slugify maps every non-alphanumeric run to "-", which would turn
    // the separator into a hyphen and make the two halves indistinguishable.
    return `${slugify(name)}_${suffix}`;
}
/**
 * Cost, ABV and standard drinks for a stored spec.
 *
 * Lines whose ingredient is unknown are costed at zero and reported, rather
 * than throwing — a library with one unmatched import line should still show
 * a cost for everything else.
 */
export function specTotals(spec, ingredientsById) {
    const unknown = [];
    const lines = spec.lines.map((line) => {
        const ing = ingredientsById.get(line.ingredientId);
        if (!ing)
            unknown.push(line.ingredientId);
        return {
            ingredientId: line.ingredientId,
            ml: line.ml,
            costPerMl: ing?.costPerMl ?? 0,
            abv: ing?.abv ?? 0,
        };
    });
    return { ...buildTotals(lines, spec.sundries), unknownIngredientIds: unknown };
}
//# sourceMappingURL=library.js.map